import React, { useState, useEffect, useRef } from "react"
import { lmsConnectionsApi } from "../../lib/api"
import AddLmsConnectionForm from "./AddLmsConnectionForm"

const LmsConnections = ({ onConnectionChange }) => {
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [syncingIds, setSyncingIds] = useState(new Set())
  const pollingIntervalsRef = useRef(new Map())

  useEffect(() => {
    fetchConnections()

    // Cleanup polling intervals on unmount
    return () => {
      pollingIntervalsRef.current.forEach((interval) => clearInterval(interval))
      pollingIntervalsRef.current.clear()
    }
  }, [])

  const fetchConnections = async () => {
    try {
      const data = await lmsConnectionsApi.list()
      setConnections(data.lms_connections)

      // Start polling for any connections that are currently syncing
      data.lms_connections.forEach((connection) => {
        if (connection.syncing) {
          startPollingForSync(connection.id)
        }
      })
    } catch (error) {
      console.error("Failed to fetch connections:", error)
    } finally {
      setLoading(false)
    }
  }

  const startPollingForSync = (connectionId) => {
    // Avoid duplicate polling using ref (synchronous check)
    if (pollingIntervalsRef.current.has(connectionId)) return

    setSyncingIds((prev) => new Set([...prev, connectionId]))

    const interval = setInterval(async () => {
      try {
        const data = await lmsConnectionsApi.get(connectionId)
        if (!data.connection.syncing) {
          clearInterval(interval)
          pollingIntervalsRef.current.delete(connectionId)
          setSyncingIds((prev) => {
            const next = new Set(prev)
            next.delete(connectionId)
            return next
          })
          fetchConnections()
          onConnectionChange?.()
        }
      } catch (error) {
        console.error("Failed to poll sync status:", error)
        clearInterval(interval)
        pollingIntervalsRef.current.delete(connectionId)
        setSyncingIds((prev) => {
          const next = new Set(prev)
          next.delete(connectionId)
          return next
        })
      }
    }, 2000)

    pollingIntervalsRef.current.set(connectionId, interval)
  }

  const handleSync = async (connectionId) => {
    try {
      await lmsConnectionsApi.sync(connectionId)
      startPollingForSync(connectionId)
    } catch (error) {
      console.error("Failed to start sync:", error)
    }
  }

  const handleUpdateToken = async (connectionId, accessToken) => {
    const data = await lmsConnectionsApi.update(connectionId, { access_token: accessToken })
    setConnections((prev) => prev.map((connection) => (
      connection.id === connectionId ? data.connection : connection
    )))

    try {
      await lmsConnectionsApi.sync(connectionId)
      startPollingForSync(connectionId)
      return { syncStarted: true }
    } catch (error) {
      return { syncStarted: false, syncError: error }
    }
  }

  const handleDelete = async (connectionId) => {
    if (!confirm("Are you sure you want to remove this LMS connection? All synced data will be deleted.")) {
      return
    }
    try {
      await lmsConnectionsApi.delete(connectionId)
      setConnections((prev) => prev.filter((c) => c.id !== connectionId))
      onConnectionChange?.()
    } catch (error) {
      console.error("Failed to delete connection:", error)
    }
  }

  const handleAddSuccess = (newConnection) => {
    setConnections((prev) => [...prev, newConnection])
    setShowAddForm(false)
    onConnectionChange?.()
    // Start polling for sync completion
    startPollingForSync(newConnection.id)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never"
    return new Date(dateStr).toLocaleString()
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="animate-pulse h-32 bg-gray-100 rounded-lg"></div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">LMS Connections</h2>
            <p className="text-sm text-gray-500">
              Connect your learning management systems to sync assignments
            </p>
          </div>
        </div>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Add Connection
          </button>
        )}
      </div>

      {connections.length === 0 && !showAddForm && (
        <div className="text-center py-8">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <p className="text-gray-500">No LMS connections configured</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            Add your first connection
          </button>
        </div>
      )}

      {showAddForm && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <h3 className="font-medium text-gray-900 mb-4">Add New Connection</h3>
          <AddLmsConnectionForm
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {connections.length > 0 && (
        <div className="space-y-4 mt-4">
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              syncing={syncingIds.has(connection.id) || connection.syncing}
              onSync={() => handleSync(connection.id)}
              onUpdateToken={(accessToken) => handleUpdateToken(connection.id, accessToken)}
              onDelete={() => handleDelete(connection.id)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const ConnectionCard = ({ connection, syncing, onSync, onUpdateToken, onDelete, formatDate }) => {
  const [editingToken, setEditingToken] = useState(false)
  const [accessToken, setAccessToken] = useState("")
  const [savingToken, setSavingToken] = useState(false)
  const [tokenError, setTokenError] = useState("")
  const [tokenStatus, setTokenStatus] = useState(null)

  const handleTokenSubmit = async (event) => {
    event.preventDefault()
    const token = accessToken.trim()
    if (!token) {
      setTokenError("Enter a Canvas API key.")
      return
    }

    setSavingToken(true)
    setTokenError("")
    setTokenStatus(null)

    try {
      const result = await onUpdateToken(token)
      setAccessToken("")
      setEditingToken(false)
      setTokenStatus(result.syncStarted ? {
        type: "success",
        message: "Canvas API key updated. A fresh sync has started.",
      } : {
        type: "warning",
        message: `Canvas API key updated, but the sync could not start: ${result.syncError.message}. Use Sync to try again.`,
      })
    } catch (error) {
      setTokenError(error.message || "Could not update the Canvas API key.")
    } finally {
      setSavingToken(false)
    }
  }

  const cancelTokenEdit = () => {
    setAccessToken("")
    setTokenError("")
    setEditingToken(false)
  }

  const statusColor = {
    never: "text-gray-500",
    syncing: "text-blue-600",
    completed: "text-green-600",
    failed: "text-red-600",
  }[connection.sync_status] || "text-gray-500"

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${connection.enabled ? "bg-green-100" : "bg-gray-100"}`}>
            <svg className={`w-5 h-5 ${connection.enabled ? "text-green-600" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{connection.name}</h3>
            <p className="text-sm text-gray-500">{connection.instance_url}</p>
            <div className="flex items-center gap-4 mt-1 text-xs">
              <span className={statusColor}>
                {connection.sync_status === "syncing" || syncing ? "Syncing..." : `Last sync: ${formatDate(connection.last_sync_at)}`}
              </span>
              <span className="text-gray-400">{connection.courses_count} courses</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onSync}
            disabled={syncing || savingToken}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync
              </>
            )}
          </button>
          {connection.provider === "canvas" && (
            <button
              type="button"
              onClick={() => {
                setEditingToken((current) => !current)
                setTokenError("")
                setTokenStatus(null)
              }}
              disabled={syncing || savingToken}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              Update API key
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {editingToken && (
        <form onSubmit={handleTokenSubmit} className="mt-4 border-t pt-4">
          <label htmlFor={`canvas-token-${connection.id}`} className="block text-sm font-medium text-gray-700">
            New Canvas API key
          </label>
          <p className="mt-1 text-xs text-gray-500">
            The key is validated before replacing the current one. Your courses, assignments, and completion history are preserved.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              id={`canvas-token-${connection.id}`}
              type="password"
              autoComplete="new-password"
              value={accessToken}
              onChange={(event) => setAccessToken(event.target.value)}
              disabled={savingToken}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100"
              placeholder="Paste the new Canvas API key"
            />
            <button
              type="submit"
              disabled={savingToken}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingToken ? "Validating..." : "Save and sync"}
            </button>
            <button
              type="button"
              onClick={cancelTokenEdit}
              disabled={savingToken}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
          {tokenError && <p role="alert" className="mt-2 text-sm text-red-600">{tokenError}</p>}
        </form>
      )}

      {tokenStatus && (
        <p
          role="status"
          className={`mt-3 text-sm ${tokenStatus.type === "success" ? "text-green-700" : "text-amber-700"}`}
        >
          {tokenStatus.message}
        </p>
      )}
    </div>
  )
}

export default LmsConnections
